import { enumManagers, UnitType_enum } from '@chirp/enums';
import { Alert, notification } from 'antd';
import { orderBy } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';

import EnumTag, { Color } from '../../../../components/EnumTag';
import {
  UnitOccupancy,
  UnitType,
  updateUnitDocument,
  updateUnitMutation,
  updateUnitMutationVariables,
  VirtualDirectoryUnitFragment,
  VirtualDirectoryUserFragment,
} from '../../../../graphql/api/generated';
import { PermissionKey_enum } from '../../../../graphql/hasura/generated';
import { authentication } from '../../../../stores';
import { formatEnumValue, Refetch } from '../../../../utils';
import { createActionsColumn } from '../../../common/columns';
import type { IChirpTableColumn } from '../../../typings';
import { UnitModel } from '../../../Unit/model';
import { UserModel } from '../../../User/model';

type VirtualDirectoryTableRow= VirtualDirectoryUserFragment | VirtualDirectoryUnitFragment;

const directoryStatusColumn: IChirpTableColumn<VirtualDirectoryTableRow> = {
  title: 'Directory Status',
  key: 'directoryStatus',
  filters: ['Listed', 'Unlisted'].map(value => ({ value, text: value })),
  render: ({ unlistedReasons }) => (
    <Alert
      showIcon
      type={unlistedReasons.length ? 'warning' : 'success'}
      message={<>{unlistedReasons.length ? 'Unlisted' : 'Listed'}</>}
    />
  ),
  renderString: ({ unlistedReasons }) => unlistedReasons.length ? 'Unlisted' : 'Listed',
};

const residentDisplayNameColumn: IChirpTableColumn<VirtualDirectoryUserFragment> = {
  title: 'Resident Display Name',
  render: ({ userId, userDisplayName }) => {
    const userLink = UserModel.routes.getRowLinkById(userId);

    return userLink ? <Link to={userLink}>{userDisplayName}</Link> : userDisplayName;
  },
};

const residentFullNameColumn: IChirpTableColumn<VirtualDirectoryUserFragment> = {
  title: 'Resident Full Name',
  render: ({ userId, userFullName }) => {
    const userLink = UserModel.routes.getRowLinkById(userId);

    return userLink ? <Link to={userLink}>{userFullName}</Link> : userFullName;
  },
};

const unitNameColumn: IChirpTableColumn<VirtualDirectoryTableRow> = {
  title: 'Unit',
  render: ({ unitId, unitDisplayName }) => {
    const unitLink = UnitModel.routes.getRowLinkById(unitId);

    return unitLink ? <Link to={unitLink}>{unitDisplayName}</Link> : unitDisplayName;
  },
};

const unitOccupancyColumn: IChirpTableColumn<VirtualDirectoryUnitFragment> = {
  title: 'Occupancy',
  key: 'occupancy',
  filters: Object.values(UnitOccupancy).map((value) => ({
    value,
    text: formatEnumValue(value),
  })),
  render: ({ occupancy }) => {
    return (
      <EnumTag
        enumValue={occupancy}
        colorMap={{
          [UnitOccupancy.OCCUPIED]: Color.Green,
          [UnitOccupancy.VACANT]: Color.Orange,
        }}
      />
    );
  },
  renderString: ({ occupancy }) => occupancy,
};

const unitTypeColumn: IChirpTableColumn<VirtualDirectoryUnitFragment> = {
  title: 'Type',
  render: ({ type }) => type ? enumManagers.UnitType.getLabel(UnitType_enum[type]) : null,
};

function formatVirtualDirectoryUnitResidents(users: VirtualDirectoryUnitFragment['users']) {
  const sortedUsers = orderBy(users, u => u.userFullName);

  return sortedUsers.map(u => ({ userId: u.userId, firstName: u.userFullName }));
}

const unitResidentsColumn: IChirpTableColumn<VirtualDirectoryUnitFragment> = {
  title: 'Residents',
  render: ({ users }) => (
    UserModel.routes.renderRowLinks(formatVirtualDirectoryUnitResidents(users))
  ),
  renderString: ({ users }) => (
    formatVirtualDirectoryUnitResidents(users).map(UserModel.labels.getLabel).join(', ')
  ),
};

export const createUnlistedReasonsColumn = (filterValues: string[])  => {
  const filters = filterValues.map(value => ({ value, text: value }));

  const unlistedReasonsColumn: IChirpTableColumn<VirtualDirectoryTableRow> = {
    title: 'Unlisted Reasons',
    key: 'unlistedReasons',
    filters: filters.length ? filters : undefined,
    render: ({ unlistedReasons }) => {
      return (
        <>
          {unlistedReasons.map((reason, index) => (
            <Alert
              key={reason}
              showIcon
              type='warning'
              message={reason}
              style={{ marginBottom: index !== (unlistedReasons.length - 1) ? 10 : 0 }}
            />
          ))}
        </>
      );
    },
    renderString: ({ unlistedReasons }) => unlistedReasons.join(', '),
  };

  return unlistedReasonsColumn;
};

export const createUnitActionsColumn = (refetch: Refetch) => {
  return createActionsColumn<VirtualDirectoryUnitFragment>({
    actions: [{
      label: ({ virtualDirectoryEnabled }) => virtualDirectoryEnabled ? 'Unlist' : 'List',
      description: 'Control whether the unit should be listed in the Virtual Directory',
      enabledByModel: () => authentication.hasPermission(PermissionKey_enum.Property_Update),
      enabledByRow: ({ type }) => type === UnitType.RESIDENTIAL,
      confirmation: {
        title: ({ virtualDirectoryEnabled }) => virtualDirectoryEnabled ? 'Unlist Unit' : 'List Unit',
        content: ({ virtualDirectoryEnabled, unitDisplayName }) => (
          virtualDirectoryEnabled
            ? `Are you sure you want to unlist ${unitDisplayName} from the Virtual Directory?`
            : `Are you sure you want to list ${unitDisplayName} in the Virtual Directory?`
        ),
      },
      executes: async (unit, { apiClient }) => {
        const { unitId, unitDisplayName } = unit;
        const virtualDirectoryEnabled = !unit.virtualDirectoryEnabled;

        await apiClient.mutate<updateUnitMutation, updateUnitMutationVariables>({
          mutation: updateUnitDocument,
          variables: {
            unitId,
            input: { virtualDirectoryEnabled },
          },
        });

        await refetch();

        notification.success({
          message: 'Success',
          description: (
            `${unitDisplayName} was successfully ${virtualDirectoryEnabled ? 'listed' : 'unlisted'}!`
          ),
        });
      },
    }],
  });
}

export const residentColumns = [
  directoryStatusColumn,
  residentFullNameColumn,
  residentDisplayNameColumn,
  unitNameColumn,
];

export const unitColumns = [
  directoryStatusColumn,
  unitNameColumn,
  unitOccupancyColumn,
  unitTypeColumn,
  unitResidentsColumn,
];
